import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { shiftLabel } from "@/lib/format";
import { Page, Tabs, Table, Tr, Td, StatusPill, money, signed, tone, Loading, Field } from "@/v2/ui";
import NewShiftButton from "@/v2/NewShift";

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const GROUPS = { all: null, draft: ["draft"], submitted: ["submitted", "manager_reviewed"], approved: ["approved", "locked"] };

export default function Shifts2() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "all";
  const monthStart = new Date().toISOString().slice(0, 8) + "01";
  const [from, setFrom] = useState(params.get("from") || monthStart);
  const [to, setTo] = useState(params.get("to") || new Date().toISOString().slice(0, 10));
  const [shifts, setShifts] = useState(null);
  useEffect(() => { api.get("/shifts").then(({ data }) => setShifts(data)).catch(() => setShifts([])); }, []);

  const inRange = useMemo(() => (shifts || []).filter((s) => s.date >= from && s.date <= to), [shifts, from, to]);
  const rows = inRange.filter((s) => !GROUPS[status] || GROUPS[status].includes(s.status));
  const n = (k) => inRange.filter((s) => GROUPS[k].includes(s.status)).length;
  const sales = rows.reduce((a, s) => a + (s.computed_totals?.total_sales || 0), 0);
  const variance = rows.reduce((a, s) => a + (s.computed_totals?.cash_variance || 0), 0);

  return (
    <Page title="Shifts" actions={<NewShiftButton />}>
      <Tabs value={status} onChange={(v) => setParams(v === "all" ? {} : { status: v })} tabs={[
        { value: "all", label: "All" }, { value: "draft", label: `Draft (${n("draft")})` },
        { value: "submitted", label: `Awaiting approval (${n("submitted")})` }, { value: "approved", label: `Approved (${n("approved")})` },
      ]} />
      <div className="mt-4 flex flex-wrap gap-3">
        <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="min-h-[40px] rounded-md border border-slate-300 px-2 text-sm text-slate-800" /></Field>
        <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="min-h-[40px] rounded-md border border-slate-300 px-2 text-sm text-slate-800" /></Field>
      </div>
      {!shifts ? <Loading /> : (
        <Table minWidth={720} cols={[{ label: "Date" }, { label: "Shift" }, { label: "Supervisor" }, { label: "Sales", align: "right" }, { label: "Cash over / short", align: "right" }, { label: "Status" }]}
          foot={rows.length > 0 && <tr><td className="px-2 py-3" colSpan={3}>Total</td><td className="px-2 py-3 text-right tabular-nums">{money(sales)}</td><td className="px-2 py-3 text-right tabular-nums">{signed(variance)}</td><td /></tr>}>
          {rows.map((s) => {
            const v = s.computed_totals?.cash_variance || 0;
            return (
              <Tr key={s._id || s.id}>
                <Td><Link to={`/v2/shift/${s._id || s.id}`} className="font-semibold text-sky-700 hover:underline">{fmtDate(s.date)}</Link></Td>
                <Td>{shiftLabel(s.shift_type, s.date)}</Td>
                <Td>{s.supervisor_name || "—"}</Td>
                <Td right>{money(s.computed_totals?.total_sales || 0)}</Td>
                <Td right className={tone(v)}>{signed(v)}</Td>
                <Td><StatusPill status={s.status} /></Td>
              </Tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-400">No shifts</td></tr>}
        </Table>
      )}
    </Page>
  );
}
